import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { 
  Clock, 
  Car, 
  TrendingDown, 
  CheckCircle, 
  Shield, 
  Users,
  Gavel,
  FileCheck,
  CreditCard,
  ThumbsUp
} from 'lucide-react';

export function HowItWorks() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-900 to-indigo-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Get Better Insurance Rates, Completely Free
          </h1>
          <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
            Instead of ringing around for quotes, let insurers compete for your business.
            Our service is 100% free for customers - no hidden fees, ever.
          </p>
        </div>
      </div>

      {/* Process Steps */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid gap-12 md:gap-16">
          {/* Step 1: Submit Details */}
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-shrink-0 bg-indigo-100 p-4 rounded-lg">
              <Car className="w-12 h-12 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Submit Your Details</h2>
              <p className="text-lg text-gray-600 mb-4">
                Fill out our simple form with your vehicle and personal information - it's completely 
                free with no obligation. We only ask for what's necessary to get you accurate quotes.
              </p>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="font-medium text-gray-900 mb-3">What you'll need:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    Vehicle registration and details
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    Driving history and license information
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    Basic personal information
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Step 2: Verification */}
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-shrink-0 bg-blue-100 p-4 rounded-lg">
              <Shield className="w-12 h-12 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Instant Verification</h2>
              <p className="text-lg text-gray-600 mb-4">
                Our system instantly verifies your vehicle details and creates your insurance request.
                We use secure, real-time data to ensure accuracy.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-2">
                    <Clock className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium">Real-time Verification</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Instant DVLA database checks
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-2">
                    <Shield className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium">Secure Process</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Bank-level data security
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Bidding */}
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-shrink-0 bg-green-100 p-4 rounded-lg">
              <Gavel className="w-12 h-12 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Live Bidding Begins</h2>
              <p className="text-lg text-gray-600 mb-4">
                Instead of you having to shop around, verified insurers compete in real-time to win your 
                business. Our unique bidding system drives prices down as insurers try to offer the most 
                competitive rates.
              </p>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-green-600 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Multiple Insurers</h4>
                      <p className="text-sm text-gray-600">Verified providers compete for your business</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <TrendingDown className="w-5 h-5 text-green-600 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">Real-time Price Updates</h4>
                      <p className="text-sm text-gray-600">Watch prices drop as insurers compete</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-green-600 mr-3" />
                    <div>
                      <h4 className="font-medium text-gray-900">24-48 Hour Window</h4>
                      <p className="text-sm text-gray-600">Plenty of time for competitive bidding</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4: Selection */}
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-shrink-0 bg-yellow-100 p-4 rounded-lg">
              <FileCheck className="w-12 h-12 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Choose Your Policy</h2>
              <p className="text-lg text-gray-600 mb-4">
                Once bidding ends, review all offers and select the best one for you. Compare prices,
                coverage options, and insurer ratings all in one place.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-2">
                    <TrendingDown className="w-5 h-5 text-yellow-600 mr-2" />
                    <span className="font-medium">Compare Prices</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    See all bids side by side
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-2">
                    <Shield className="w-5 h-5 text-yellow-600 mr-2" />
                    <span className="font-medium">Coverage Details</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Review policy features
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-100">
                  <div className="flex items-center mb-2">
                    <ThumbsUp className="w-5 h-5 text-yellow-600 mr-2" />
                    <span className="font-medium">Insurer Ratings</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Check customer reviews
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 5: Payment */}
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-shrink-0 bg-purple-100 p-4 rounded-lg">
              <CreditCard className="w-12 h-12 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Secure Payment</h2>
              <p className="text-lg text-gray-600 mb-4">
                Complete your purchase directly with your chosen insurer. You'll get flexible payment
                options and instant policy documentation from the insurance provider.
              </p>
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="font-medium text-gray-900 mb-3">Payment Features:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-purple-500 mr-2" />
                    Secure payment processing
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-purple-500 mr-2" />
                    Monthly or annual payment options
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-purple-500 mr-2" />
                    Instant policy documents
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Choose CoverBids?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <TrendingDown className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Save Money</h3>
              <p className="text-gray-600">
                Our free bidding system creates competition between insurers, driving down prices without any cost to you.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Clock className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Save Time</h3>
              <p className="text-gray-600">
                No need to visit multiple websites. Get all your quotes in one place.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Shield className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Stay Protected</h3>
              <p className="text-gray-600">
                All our insurers are FCA regulated and thoroughly vetted for quality.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Ready to Get Your Free Insurance Quotes?
          </h2>
          <a
            href="/#quote-form"
            className="inline-block bg-indigo-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-indigo-700 transition-colors"
          >
            Compare Quotes For Free
          </a>
          <p className="mt-4 text-gray-500">
            100% Free • No Hidden Fees • No Obligation
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}